import type { UsageRequestLogEntry, UsageRequestUnitBilling } from '@nadoutong/contracts';
import { Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { PlatformApiError } from '../api/client.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageRequests } from '../api/useUsage.ts';
import { Skeleton } from '../components/Skeleton.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import {
  formatTimestamp,
  formatTokens,
  formatUsdMicrosOrCentsAsCnyMetered,
  unitAwareTokenCell,
} from '../components/formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Input, PageHeader, Panel, cx } from '../ui/index.ts';

/**
 * `/requests` — request log explorer (epic #950 PR-5, Field Notes
 * reskin of the local page). Wire behavior preserved: range picker
 * (24h/7d/30d radiogroup), request-id / key / status filters, 50-row
 * paging, and the click-to-expand technical detail row.
 */

const PAGE_SIZE = 50;

const SELECT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

type RangeOption = {
  readonly id: string;
  readonly labelKey: I18nKey;
  readonly days: number;
};

const RANGE_OPTIONS: readonly RangeOption[] = [
  { id: '24h', labelKey: 'requests.range.24h', days: 1 },
  { id: '7d', labelKey: 'requests.range.7d', days: 7 },
  { id: '30d', labelKey: 'requests.range.30d', days: 30 },
];

function rangeFromDays(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function Requests(): JSX.Element {
  const t = useT();
  const keysQ = useKeysList();
  const displayRateQ = useDisplayRate();
  const [selected, setSelected] = useState<RangeOption>(
    RANGE_OPTIONS[1] ?? RANGE_OPTIONS[0] ?? { id: '7d', labelKey: 'requests.range.7d', days: 7 },
  );
  const [requestIdInput, setRequestIdInput] = useState('');
  const [requestId, setRequestId] = useState('');
  const [apiKeyId, setApiKeyId] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const range = useMemo(() => rangeFromDays(selected.days), [selected]);
  const requestsQ = useUsageRequests({
    ...range,
    requestId,
    apiKeyId,
    status: status === 'success' || status === 'error' ? status : undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const rows = requestsQ.data?.rows ?? [];
  const total = requestsQ.data?.total ?? 0;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  function resetPaging(): void {
    setOffset(0);
    setExpandedId(null);
  }

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setRequestId(requestIdInput.trim());
    resetPaging();
  }

  return (
    <section data-testid="requests-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('requests.title')}
        sub={t('requests.subtitle')}
        testId="requests-header"
        actions={
          <div
            role="radiogroup"
            aria-label={t('requests.range.aria_label')}
            data-testid="requests-range-picker"
            className="inline-flex items-center gap-1 rounded-lg border border-line bg-card p-1"
          >
            {RANGE_OPTIONS.map((opt) => {
              const isActive = opt.id === selected.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => {
                    setSelected(opt);
                    resetPaging();
                  }}
                  data-testid={`requests-range-${opt.id}`}
                  className={cx(
                    'inline-flex items-center justify-center rounded px-3 py-1 text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
                    isActive ? 'bg-forest text-on-forest' : 'text-ink-mid hover:text-ink',
                  )}
                >
                  {t(opt.labelKey)}
                </button>
              );
            })}
          </div>
        }
      />

      <Panel caption={t('requests.title')} padded testId="requests-card">
        <div className="flex flex-col gap-4">
          <form
            className="grid gap-4 lg:grid-cols-[minmax(220px,1.4fr)_minmax(180px,1fr)_minmax(150px,0.7fr)_auto]"
            onSubmit={handleSearch}
          >
            <label className="flex flex-col gap-1.5">
              <span className="text-label text-ink-mid">{t('requests.filter.request_id')}</span>
              <Input
                mono
                value={requestIdInput}
                onChange={(e) => setRequestIdInput(e.currentTarget.value)}
                placeholder={t('requests.filter.request_id_placeholder')}
                data-testid="requests-filter-request-id"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-label text-ink-mid">{t('requests.filter.key')}</span>
              <select
                value={apiKeyId}
                onChange={(e) => {
                  setApiKeyId(e.currentTarget.value);
                  resetPaging();
                }}
                data-testid="requests-filter-key"
                className={SELECT_CLASS}
              >
                <option value="">{t('requests.filter.key_all')}</option>
                {(keysQ.data?.keys ?? []).map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.name ?? t('requests.key.unnamed')}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-label text-ink-mid">{t('requests.filter.status')}</span>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.currentTarget.value);
                  resetPaging();
                }}
                data-testid="requests-filter-status"
                className={SELECT_CLASS}
              >
                <option value="">{t('requests.filter.status_all')}</option>
                <option value="success">{t('requests.filter.status_success')}</option>
                <option value="error">{t('requests.filter.status_error')}</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <Button type="submit" variant="primary" data-testid="requests-filter-submit">
                <Search aria-hidden="true" className="h-3.5 w-3.5" />
                {t('requests.filter.submit')}
              </Button>
              {requestId.length > 0 ? (
                <Button
                  variant="secondary"
                  data-testid="requests-filter-clear"
                  onClick={() => {
                    setRequestId('');
                    setRequestIdInput('');
                    resetPaging();
                  }}
                >
                  {t('requests.filter.clear')}
                </Button>
              ) : null}
            </div>
          </form>

          {requestsQ.isLoading ? (
            <div className="flex flex-col gap-2" data-testid="requests-loading">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
              <span className="sr-only">{t('requests.loading')}</span>
            </div>
          ) : requestsQ.isError ? (
            <p className="m-0 text-body text-bad" role="alert" data-testid="requests-error">
              {requestsQ.error instanceof PlatformApiError
                ? `${requestsQ.error.code}: ${requestsQ.error.message}`
                : t('requests.error')}
            </p>
          ) : rows.length === 0 ? (
            <p className="m-0 text-body text-ink-low" data-testid="requests-empty">
              {t('requests.empty')}
            </p>
          ) : (
            <>
              <RequestTable
                rows={rows}
                expandedId={expandedId}
                cnyPerUsd={cnyPerUsd}
                onToggle={(id) => setExpandedId((current) => (current === id ? null : id))}
              />
              <footer className="flex flex-wrap items-center justify-between gap-3 text-caption text-ink-low">
                <span data-testid="requests-page-count">
                  {t('requests.pagination.summary', {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
                </span>
                <div className="inline-flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canPrev}
                    onClick={() => {
                      setOffset(Math.max(0, offset - PAGE_SIZE));
                      setExpandedId(null);
                    }}
                    data-testid="requests-page-prev"
                  >
                    {t('requests.pagination.prev')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!canNext}
                    onClick={() => {
                      setOffset(offset + PAGE_SIZE);
                      setExpandedId(null);
                    }}
                    data-testid="requests-page-next"
                  >
                    {t('requests.pagination.next')}
                  </Button>
                </div>
              </footer>
            </>
          )}
        </div>
      </Panel>
    </section>
  );
}

/**
 * Under async rating (#1410 P3) a request is visible before its price is
 * known, so rendering the formatted zero would tell a paying customer the
 * request was free minutes before the charge lands.
 *
 * `ratingStatus` is optional on the wire: a gateway that predates P3, or the
 * one-deploy window where platform has shipped and the gateway has not, omits
 * it. Every such row is final — hence `'rated'` as the fallback, never
 * `'pending'`, which would paint the entire history as unsettled.
 */
function isRatingPending(row: UsageRequestLogEntry): boolean {
  return (row.ratingStatus ?? 'rated') === 'pending';
}

/** #1670 token-column discriminator; the shared logic lives in formatters. */
function tokenCell(row: UsageRequestLogEntry, count: number): string {
  return unitAwareTokenCell(row, formatTokens(count));
}

/** `3 images · ¥x` / `Not delivered — not charged` unit billing summary. */
function unitBillingSummary(
  unitBilling: UsageRequestUnitBilling,
  cnyPerUsd: number | null,
  t: ReturnType<typeof useT>,
): string {
  if (unitBilling.kind === 'not_charged') {
    return t(`requests.unit_billing.not_charged.${unitBilling.reason}`);
  }
  return t('requests.unit_billing.charged', {
    quantity: unitBilling.quantity,
    unit: t(`requests.unit_billing.unit.${unitBilling.unit}`),
    amount: formatUsdMicrosOrCentsAsCnyMetered(unitBilling.amountUsdMicros, 0, cnyPerUsd),
  });
}

function RequestTable({
  rows,
  expandedId,
  cnyPerUsd,
  onToggle,
}: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly expandedId: string | null;
  readonly cnyPerUsd: number | null;
  readonly onToggle: (id: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1180px] border-collapse text-body" data-testid="requests-table">
        <thead>
          <tr className="border-b border-line text-left">
            <RequestTh>{t('requests.column.time')}</RequestTh>
            <RequestTh>{t('requests.column.request')}</RequestTh>
            <RequestTh>{t('requests.column.key')}</RequestTh>
            <RequestTh>{t('requests.column.model')}</RequestTh>
            <RequestTh>{t('requests.column.status')}</RequestTh>
            <RequestTh>{t('requests.column.duration')}</RequestTh>
            <RequestTh>{t('requests.column.first_byte')}</RequestTh>
            <RequestTh>{t('requests.column.input')}</RequestTh>
            <RequestTh>{t('requests.column.output')}</RequestTh>
            <RequestTh>{t('requests.column.cache_read')}</RequestTh>
            <RequestTh>{t('requests.column.cache_write')}</RequestTh>
            <RequestTh>{t('requests.column.cost')}</RequestTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedId === row.id;
            return (
              <Fragment key={row.id}>
                <tr
                  className="cursor-pointer border-b border-line-soft transition-colors duration-150 last:border-b-0 hover:bg-active/40"
                  data-testid={`requests-row-${row.id}`}
                  onClick={() => onToggle(row.id)}
                >
                  <td className="px-3 py-2 font-mono text-ink-low">
                    {formatTimestamp(row.createdAt)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink">{shortRequestId(row.requestId)}</td>
                  <td className="px-3 py-2 font-medium text-ink">
                    {keyDisplayName(row.apiKeyName, row.apiKeyPrefix, t)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    <span className="flex flex-col gap-1">
                      <span>{row.model}</span>
                      <span className="text-mono-label text-ink-low">{row.vendor}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip
                      variant={row.statusCode >= 400 ? 'error' : 'ok'}
                      label={String(row.statusCode)}
                      testId={`requests-status-${row.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {row.latencyMs === null
                      ? '—'
                      : t('requests.duration.ms', { value: row.latencyMs })}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {row.clientResponseElapsedMs === null
                      ? '—'
                      : t('requests.duration.ms', { value: row.clientResponseElapsedMs })}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.inputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.outputTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.cacheReadTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-mid">
                    {tokenCell(row, row.cacheWriteTokens)}
                  </td>
                  <td className="px-3 py-2 font-mono text-forest">
                    {isRatingPending(row) ? (
                      <span className="text-ink-low">{t('requests.cost.pending')}</span>
                    ) : row.unitBilling?.kind === 'not_charged' ? (
                      // Wording is reason-specific on purpose: `not_delivered`
                      // is final ("Not charged"), while `pricing_review` is a
                      // reversible hold — saying "not charged" there would
                      // promise an outcome the review may still change.
                      <span
                        className="text-ink-low"
                        data-testid={`requests-cost-not-charged-${row.id}`}
                      >
                        {t(`requests.cost.not_charged.${row.unitBilling.reason}`)}
                      </span>
                    ) : (
                      formatUsdMicrosOrCentsAsCnyMetered(row.costUsdMicros, row.costUsdCents, cnyPerUsd)
                    )}
                  </td>
                </tr>
                {isExpanded ? (
                  <tr key={`${row.id}-details`} data-testid={`requests-row-details-${row.id}`}>
                    <td colSpan={12} className="border-b border-line-soft bg-raised px-3 py-3">
                      <dl className="m-0 grid gap-3 text-caption sm:grid-cols-2 lg:grid-cols-4">
                        <Detail label={t('requests.detail.request_id')} value={row.requestId} />
                        <Detail label={t('requests.detail.api_key_id')} value={row.apiKeyId} />
                        <Detail label={t('requests.detail.protocol')} value={row.protocol} />
                        {row.unitBilling !== undefined ? (
                          <Detail
                            label={t('requests.detail.unit_billing')}
                            value={unitBillingSummary(row.unitBilling, cnyPerUsd, t)}
                            testId={`requests-detail-unit-billing-${row.id}`}
                          />
                        ) : null}
                      </dl>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RequestTh({ children }: { readonly children: string }): JSX.Element {
  return (
    <th className="px-3 py-2 font-mono text-mono-label font-medium uppercase text-ink-low">
      {children}
    </th>
  );
}

function Detail({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={testId}>
      <dt className="font-mono text-mono-label uppercase text-ink-low">{label}</dt>
      <dd className="m-0 break-all font-mono text-ink-mid">{value}</dd>
    </div>
  );
}

function shortRequestId(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('requests.key.unnamed');
  return t('requests.key.unknown');
}
